import { ForkKnife } from '@phosphor-icons/react';
import { Icon, Surface, cn } from '@shh/ui';
import { useNavigate } from 'react-router';
import { useFamily } from '../data/family';
import { useMealTime, useMeals } from '../data/meals';
import { useRecipe } from '../data/recipes';
import { useLens } from '../app/lens';
import { ideasText } from '../i18n/ideas';
import { mealsText as t } from '../i18n/meals';
import { usePlan } from './Plus';

/**
 * "Heute gibt's · 18:30" (Küche 3, Plus) on Heute and the wall: a tap opens the recipe – or
 * the plan, for free text. Without Plus or without a plan the card simply is not there (Konzept:
 * "An der Wand verschwindet ‚Heute gibt's' einfach").
 */
export function TodayMeal({ className }: { className?: string }) {
  const f = useFamily();
  const plan = usePlan();
  const mealTime = useMealTime();
  const meals = useMeals(f.today, f.today);
  const navigate = useNavigate();
  const lens = useLens();
  const meal = meals?.get(f.today);
  const recipe = useRecipe(meal?.recipeId ?? undefined);
  if (!plan.plus || !meals) return null;
  if (!meal) {
    // nothing planned yet (1.22): one tap to the suggestions – for whoever plans, not the wall
    const canPlan = !lens.isWall && (lens.canParent || lens.me?.role === 'teen');
    if (!canPlan) return null;
    return (
      <Surface className={cn('relative flex items-center gap-gap-md', className)}>
        <Icon
          icon={ForkKnife}
          size="lg"
          weight="duotone"
          className="shrink-0 text-accent-tertiary"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-bold text-text-secondary">{ideasText.notPlannedTitle}</span>
          <h3 className="font-display text-lg font-black">
            <button
              type="button"
              onClick={() => void navigate('/essen/heute')}
              className="cursor-pointer text-left outline-none after:absolute after:inset-0 after:rounded-card focus-visible:after:outline-3 focus-visible:after:outline-border-focus"
            >
              {ideasText.notPlannedLink}
            </button>
          </h3>
        </div>
      </Surface>
    );
  }
  return (
    <Surface className={cn('relative flex items-center gap-gap-md', className)}>
      {/* the dish's photo, when there is one – the kids at the wall see what is coming */}
      {recipe?.thumb ? (
        <img
          src={recipe.thumb}
          alt=""
          className="h-14 w-14 shrink-0 rounded-control object-cover"
        />
      ) : (
        <Icon
          icon={ForkKnife}
          size="lg"
          weight="duotone"
          className="shrink-0 text-accent-tertiary"
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-bold text-text-secondary">
          {t.todayMeal(meal.time ?? mealTime)}
        </span>
        <h3 className="font-display text-lg font-black">
          <button
            type="button"
            onClick={() =>
              void navigate(meal.recipeId ? `/essen/rezepte/${meal.recipeId}` : '/essen/woche')
            }
            className="cursor-pointer text-left outline-none after:absolute after:inset-0 after:rounded-card focus-visible:after:outline-3 focus-visible:after:outline-border-focus"
          >
            {meal.title}
          </button>
        </h3>
        {meal.note && <span className="truncate text-sm text-text-secondary">{meal.note}</span>}
      </div>
    </Surface>
  );
}
